import React from 'react';
import { Controller } from 'react-hook-form';
import { CustomChipInputField, TextField } from '@biq/apex-ui';
import { MultilineTextField } from '../../components';
import { validationsMessages } from '../../lib';
import { Grid, Typography } from '@mui/material';
import {
    glossaryMetadataFormStyles,
    glossaryValidationMessages,
} from './glossary-metadata-helper';
import { AttributeAssociationItem } from '../edge-definitions/edge-definition-helper';
import { PairedInputSelectionWidget } from '../../components/paired-input-selection-widget/PairedInputSelectionWidget';

interface GlossaryMetadataProps {
    control: any;
    errors: any;
    isEdit?: boolean;
    aliases: string[];
    abbreviations: string[];
    onAliasesChanged: (value: string[]) => void;
    onAbbreviationsChanged: (value: string[]) => void;
    onPropertiesListChanged: (newList: AttributeAssociationItem[]) => void;
    glossaryAssociationList: AttributeAssociationItem[];
    term?: string;
}

export const GlossaryMetadataForm = ({
    term,
    control,
    errors,
    aliases,
    abbreviations,
    isEdit = false,
    onAliasesChanged,
    onAbbreviationsChanged,
    onPropertiesListChanged,
    glossaryAssociationList,
}: GlossaryMetadataProps): React.JSX.Element => {
    const handleAssociationListChanged = (
        list: AttributeAssociationItem[]
    ): void => {
        onPropertiesListChanged(list);
    };

    return (
        <Grid
            sx={
                isEdit
                    ? glossaryMetadataFormStyles.generalInfoContainer
                    : undefined
            }
        >
            <Grid
                sx={
                    isEdit
                        ? glossaryMetadataFormStyles.editFormContainer
                        : glossaryMetadataFormStyles.formContainer
                }
            >
                <Grid sx={glossaryMetadataFormStyles.itemContainer}>
                    {isEdit ? (
                        <Grid
                            sx={glossaryMetadataFormStyles.infoDisplayContainer}
                        >
                            <Typography
                                sx={glossaryMetadataFormStyles.sectionLabel}
                            >
                                Name
                            </Typography>
                            <Typography
                                sx={glossaryMetadataFormStyles.sectionValue}
                            >
                                {term ?? '--'}
                            </Typography>
                        </Grid>
                    ) : (
                        <Controller
                            name='term'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <TextField
                                    {...field}
                                    variant='outlined'
                                    label='Term'
                                    required
                                    error={!!errors.term}
                                    errorText={errors?.term?.message ?? ''}
                                />
                            )}
                        />
                    )}
                </Grid>
                <Grid sx={glossaryMetadataFormStyles.itemContainer}>
                    <MultilineTextField
                        control={control}
                        name='description'
                        label='Description'
                        error={!!errors.description}
                        errorText={errors?.description?.message ?? ''}
                    />
                </Grid>
                <Grid sx={glossaryMetadataFormStyles.itemContainer}>
                    <Controller
                        name='groupName'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                label='Group Name'
                                error={!!errors.groupName}
                                errorText={errors?.groupName?.message ?? ''}
                            />
                        )}
                    />
                </Grid>
                <Grid sx={glossaryMetadataFormStyles.itemContainer}>
                    <Controller
                        name='type'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                label='Type'
                                error={!!errors.type}
                                errorText={errors?.type?.message ?? ''}
                            />
                        )}
                    />
                </Grid>
                <Grid sx={glossaryMetadataFormStyles.itemContainer}>
                    <Controller
                        name='abbreviations'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <CustomChipInputField
                                {...field}
                                required={false}
                                fieldValue={abbreviations}
                                setFieldValue={onAbbreviationsChanged}
                                size='medium'
                                label='Abbreviations'
                                helperText='Multiple values may be entered separated by commas.'
                                error={!!errors.abbreviations}
                                errorText={errors?.abbreviations?.message ?? ''}
                                customStyles={
                                    glossaryMetadataFormStyles.customChipInputStyles
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid sx={glossaryMetadataFormStyles.itemContainer}>
                    <Controller
                        name='aliases'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <CustomChipInputField
                                {...field}
                                required={false}
                                fieldValue={aliases}
                                setFieldValue={onAliasesChanged}
                                size='medium'
                                label='Aliases'
                                helperText='Multiple values may be entered separated by commas.'
                                error={!!errors.aliases}
                                errorText={errors?.aliases?.message ?? ''}
                                customStyles={
                                    glossaryMetadataFormStyles.customChipInputStyles
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid sx={glossaryMetadataFormStyles.propertiesContainer}>
                    <PairedInputSelectionWidget
                        attributeAssociationList={glossaryAssociationList}
                        handleAttributeAssociationsChanged={
                            handleAssociationListChanged
                        }
                        title={'Properties'}
                        addButtonLabel={'Property'}
                        duplicateErrorMessage={
                            glossaryValidationMessages.duplicatedOptions
                        }
                        emptyErrorMessage={validationsMessages.required}
                        leftSideInputLabel={'Name'}
                        rightSideInputLabel={'Value'}
                        checkForDuplicates={true}
                        leftSideInputStyles={
                            isEdit
                                ? glossaryMetadataFormStyles.propertiesEditInputContainer
                                : glossaryMetadataFormStyles.propertiesInputContainer
                        }
                        rightSideInputStyles={
                            isEdit
                                ? glossaryMetadataFormStyles.propertiesEditInputContainer
                                : glossaryMetadataFormStyles.propertiesInputContainer
                        }
                        customInputSelectionStyles={
                            glossaryMetadataFormStyles.propertiesInputMainContainer
                        }
                        displayEmptyState={false}
                        addButtonContainerStyles={
                            glossaryMetadataFormStyles.addPropertiesButtonContainer
                        }
                        addButtonStyles={
                            glossaryMetadataFormStyles.addPropertiesButton
                        }
                        titleContainerStyles={
                            glossaryMetadataFormStyles.titleContainer
                        }
                        widgetMainContainerStyles={
                            glossaryMetadataFormStyles.propertiesContainer
                        }
                        deleteButtonContainerStyles={
                            glossaryMetadataFormStyles.deleteButtonContainer
                        }
                        deleteButtonStyles={
                            glossaryMetadataFormStyles.deleteButton
                        }
                    />
                </Grid>
            </Grid>
        </Grid>
    );
};
